import './style.css';
import App, { ThankYouPage } from './App.js';
import { project } from './config/project.js';
import { initAnalytics, track } from './lib/analytics.js';
import { initNav } from './lib/nav.js';
import { formatPhoneDisplay, toE164ish, validateLead } from './lib/validation.js';

const routes = {
  '/': 'home',
  '/register': 'register',
  '/contact': 'register',
  '/thank-you': 'thank-you',
  '/privacy-policy': 'privacy-policy',
};

let formStarted = false;
let submitting = false;

const ATTR_KEY = 'cs_attr';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

function headerOffset() {
  const header = document.querySelector('.site-header');
  return (header?.offsetHeight || 72) + 8;
}

export function scrollToId(id, attempts = 24) {
  const el = document.querySelector(`.app #${CSS.escape(id)}`) || document.getElementById(id);
  if (!el) {
    if (attempts > 0) requestAnimationFrame(() => scrollToId(id, attempts - 1));
    return;
  }
  const top = el.getBoundingClientRect().top;
  const offset = headerOffset();
  if (top > offset + 24 || top < 0) {
    const y = top + window.pageYOffset - offset;
    window.scrollTo({ top: Math.max(0, y), behavior: 'auto' });
  }
  if (attempts > 0) setTimeout(() => scrollToId(id, attempts - 1), 60);
}

function currentUtm() {
  const params = new URLSearchParams(window.location.search);
  const utm = {};
  UTM_KEYS.forEach((k) => {
    if (params.get(k)) utm[k] = params.get(k);
  });
  return utm;
}

function persistAttribution() {
  const utm = currentUtm();
  const live = {
    utm,
    landingPage: window.location.href.split('#')[0],
    referrer: document.referrer || '',
  };
  try {
    const saved = JSON.parse(sessionStorage.getItem(ATTR_KEY) || '{}');
    if (saved.landingPage) live.landingPage = saved.landingPage;
    if (saved.referrer && !live.referrer) live.referrer = saved.referrer;
    if (utm.utm_source) live.utm = { ...(saved.utm || {}), ...utm };
    else if (saved.utm?.utm_source) live.utm = saved.utm;
  } catch {
    /* private mode */
  }
  if (live.utm?.utm_source || live.referrer) {
    try {
      sessionStorage.setItem(ATTR_KEY, JSON.stringify(live));
    } catch {
      /* private mode */
    }
  }
}

function getAttribution() {
  const liveUtm = currentUtm();
  if (liveUtm.utm_source) {
    return {
      utm: liveUtm,
      landingPage: window.location.href.split('#')[0],
      referrer: document.referrer || '',
    };
  }
  try {
    const saved = JSON.parse(sessionStorage.getItem(ATTR_KEY) || '{}');
    if (saved.utm?.utm_source || saved.landingPage) return { ...saved, utm: { ...saved.utm, ...liveUtm } };
  } catch {
    /* ignore */
  }
  return {
    utm: liveUtm,
    landingPage: window.location.href.split('#')[0],
    referrer: document.referrer || '',
  };
}

function withStoredUtm(href) {
  const next = new URL(href, window.location.origin);
  const attr = getAttribution();
  UTM_KEYS.forEach((k) => {
    if (!next.searchParams.get(k) && attr.utm?.[k]) next.searchParams.set(k, attr.utm[k]);
  });
  return next.pathname + next.search + next.hash;
}

function renderApp() {
  const app = document.getElementById('app');
  if (!app) return;
  app.innerHTML = App();
  wirePage();
}

function renderThankYou() {
  const app = document.getElementById('app');
  if (!app) return;
  app.innerHTML = ThankYouPage();
}

function handleRoute(path) {
  persistAttribution();
  const clean = path.split('?')[0].replace(/\/$/, '') || '/';
  if (clean === '/privacy-policy') {
    window.location.href = '/privacy-policy';
    return;
  }
  if (clean === '/thank-you' || routes[clean] === 'thank-you') {
    document.title = 'Thank you | Cornerstone by Primont';
    renderThankYou();
    return;
  }
  renderApp();
  const attr = getAttribution();
  const source = (attr.utm?.utm_source || '').toLowerCase();
  const note = document.getElementById('ai-source-note');
  if (note && (source.includes('chatgpt') || source.includes('openai') || source.includes('perplexity') || source.includes('gemini'))) {
    note.hidden = false;
    note.textContent =
      'You found Cornerstone through an AI assistant. Register below for floor plans, pricing, and VIP updates.';
  }
  if (clean === '/register' || clean === '/contact') {
    document.title = 'Register | Cornerstone by Primont | New Homes in Brampton';
  } else {
    document.title = project.seo?.title || document.title;
  }
  // /register and /contact are the same page as home: canonical always points to home.
  if (clean === '/' || clean === '/register' || clean === '/contact') {
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', `${project.siteUrl}/`);
  }
  const hash = window.location.hash.replace('#', '');
  const target =
    routes[clean] === 'register' ? 'register' : hash || (clean === '/' ? null : clean.replace('/', ''));
  if (target) {
    requestAnimationFrame(() => scrollToId(target));
  }
}

function showErrors(errors) {
  const box = document.getElementById('form-error-summary');
  const status = document.getElementById('form-status');
  if (!box) return;
  if (!errors.length) {
    box.hidden = true;
    box.innerHTML = '';
    return;
  }
  track('form_error', { count: errors.length });
  box.hidden = false;
  box.innerHTML = `<p><strong>${errors.length} ${errors.length === 1 ? 'error' : 'errors'} need attention</strong></p><ul>${errors
    .map((e) => `<li><a href="#${e.field}">${e.message}</a></li>`)
    .join('')}</ul>`;
  box.focus();
  if (status) status.textContent = '';
}

async function submitLead(payload) {
  const res = await fetch('/api/lead', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });
  let data = {};
  try {
    data = await res.json();
  } catch {
    data = {};
  }
  return { res, data };
}

function wireForm() {
  const form = document.getElementById('register-form');
  if (!form) return;

  form.addEventListener(
    'focusin',
    () => {
      if (!formStarted) {
        formStarted = true;
        track('form_start');
      }
    },
    true,
  );

  const phone = form.querySelector('#phone');
  phone?.addEventListener('input', (e) => {
    const digits = e.target.value.replace(/\D/g, '');
    // Only auto-format when it looks like a NANP number in progress
    if (digits.length <= 10 && !e.target.value.trim().startsWith('+')) {
      e.target.value = formatPhoneDisplay(e.target.value);
    }
  });

  const realtorRadios = form.querySelectorAll('input[name="isRealtor"]');
  const brokerageField = document.getElementById('brokerage-field');
  const brokerageInput = document.getElementById('brokerage');
  realtorRadios.forEach((r) => {
    r.addEventListener('change', () => {
      const yes = form.querySelector('input[name="isRealtor"]:checked')?.value === 'yes';
      if (brokerageField) brokerageField.hidden = !yes;
      if (brokerageInput) {
        brokerageInput.required = yes;
        if (!yes) brokerageInput.value = '';
      }
    });
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!navigator.onLine) {
      const status = document.getElementById('form-status');
      if (status) status.textContent = 'You appear to be offline. Please reconnect and try again.';
      return;
    }

    const fd = new FormData(form);
    const raw = Object.fromEntries(fd.entries());
    const errors = validateLead(raw);
    showErrors(errors);
    if (errors.length) return;

    submitting = true;
    const btn = document.getElementById('submit-btn');
    const prev = btn?.textContent;
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Submitting…';
    }

    const attr = getAttribution();
    const payload = {
      firstName: String(raw.firstName).trim(),
      lastName: String(raw.lastName).trim(),
      email: String(raw.email).trim(),
      phone: toE164ish(String(raw.phone)),
      phoneDisplay: String(raw.phone).trim(),
      isRealtor: raw.isRealtor,
      brokerage: raw.brokerage ? String(raw.brokerage).trim() : '',
      productInterest: raw.productInterest,
      buyerType: raw.buyerType,
      marketingConsent: raw.marketingConsent === 'yes',
      formType: raw.downloadId ? 'gated_download' : 'registration',
      downloadId: raw.downloadId || null,
      website: raw.website || '',
      projectName: project.name,
      builderName: project.builder,
      websiteSource: project.websiteSourceTag,
      pageUrl: window.location.href,
      ...attr,
      timestamp: new Date().toISOString(),
    };

    try {
      const { res, data } = await submitLead(payload);
      if (!res.ok || !data.ok) {
        const status = document.getElementById('form-status');
        if (status) {
          status.textContent =
            data.error === 'rate_limited'
              ? 'Too many attempts. Please wait a minute and try again.'
              : 'We could not confirm your registration. Please try again or call us.';
        }
        return;
      }

      track('generate_lead', {
        form_type: payload.formType,
        product_interest: payload.productInterest,
        buyer_type: payload.buyerType,
        is_realtor: payload.isRealtor,
        download_id: payload.downloadId || undefined,
      });

      if (payload.downloadId && data.downloadToken) {
        track('download_complete', { download_id: payload.downloadId });
        const a = document.createElement('a');
        a.href = `/api/download?token=${encodeURIComponent(data.downloadToken)}`;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }

      window.history.pushState({}, '', '/thank-you');
      renderThankYou();
    } catch {
      const status = document.getElementById('form-status');
      if (status) status.textContent = 'A network error occurred. Please try again.';
    } finally {
      submitting = false;
      if (btn) {
        btn.disabled = false;
        btn.textContent = prev || 'Submit registration';
      }
    }
  });
}

function wireDownloads() {
  const modal = document.getElementById('download-modal');
  const label = document.getElementById('download-type-label');
  const field = document.getElementById('download-id-field');
  const goto = document.getElementById('download-goto-form');
  let lastFocus = null;

  function openModal(id, title) {
    lastFocus = document.activeElement;
    if (field) field.value = id;
    if (label) label.textContent = title || id;
    if (!modal) {
      scrollToId('register');
      return;
    }
    modal.hidden = false;
    document.body.classList.add('nav-open');
    const close = modal.querySelector('.modal-close');
    close?.focus();
    track('download_request', { download_id: id });
  }

  function closeModal() {
    if (!modal) return;
    modal.hidden = true;
    document.body.classList.remove('nav-open');
    lastFocus?.focus?.();
  }

  document.querySelectorAll('[data-download]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-download');
      const title = btn.closest('.download-item')?.querySelector('h3')?.textContent || id;
      openModal(id, title);
    });
  });

  modal?.querySelector('.modal-close')?.addEventListener('click', closeModal);
  goto?.addEventListener('click', () => {
    closeModal();
    scrollToId('register');
    document.getElementById('firstName')?.focus();
  });
  modal?.addEventListener('click', (e) => {
    if (e.target === modal) closeModal();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal && !modal.hidden) closeModal();
  });
}

function wireAnalyticsClicks() {
  document.querySelectorAll('[data-analytics]').forEach((el) => {
    el.addEventListener('click', () => track(el.getAttribute('data-analytics')));
  });
  const video = document.getElementById('community-video');
  if (video) {
    let started = false;
    video.addEventListener('play', () => {
      if (!started) {
        started = true;
        track('video_start');
      }
    });
    video.addEventListener('ended', () => track('video_complete'));
  }
  const siteplan = document.querySelector('[data-analytics-view="siteplan_view"]');
  if (siteplan && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            track('siteplan_view');
            io.disconnect();
          }
        });
      },
      { threshold: 0.4 },
    );
    io.observe(siteplan);
  }
}

function wirePage() {
  initNav({ scrollToId, headerOffset });
  wireForm();
  wireDownloads();
  wireAnalyticsClicks();
}

function initRouter() {
  persistAttribution();
  handleRoute(window.location.pathname + window.location.search);
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a');
    if (!link) return;
    const href = link.getAttribute('href');
    if (!href || href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
    if (href === '/privacy-policy' || href.startsWith('/privacy-policy')) return;
    if (href.startsWith('/brampton-new-townhomes') || href.startsWith('/northwest-brampton-new-homes')) return;
    if (href.startsWith('/hst-rebate-new-homes-ontario')) return;
    if (href.startsWith('/assets')) return;
    if (href.startsWith('#')) return;
    if (href.startsWith('/')) {
      e.preventDefault();
      const next = withStoredUtm(href);
      window.history.pushState({}, '', next);
      handleRoute(next);
    }
  });
  window.addEventListener('popstate', () => handleRoute(window.location.pathname + window.location.search));
}

function boot() {
  // The live homepage injects this module after first paint. DOMContentLoaded
  // has already fired by then, so waiting for it leaves #app empty forever.
  initAnalytics();
  document.getElementById('static-lcp')?.remove();
  document.querySelector('.static-geo')?.remove();
  initRouter();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
